import { $convertToMarkdownString } from "@lexical/markdown";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useEffect, useRef } from "react";
import { useAutosaveContext } from "./AutosaveContext";
import { PLAYGROUND_TRANSFORMERS } from "./markdownTransformers";
import { normalizeExportedMarkdown } from "./preprocessMarkdown";

export {
  AutosaveContext,
  useAutosaveContext,
  useOptionalAutosaveContext,
} from "./AutosaveContext";

interface AutoSavePluginProps {
  throttleMs?: number;
  onChange: (value: string) => void;
  triggerAutosaveRef?: React.MutableRefObject<(() => void) | null>;
}

const AutoSavePlugin = ({
  throttleMs = 1000,
  onChange,
  triggerAutosaveRef,
}: AutoSavePluginProps) => {
  const [editor] = useLexicalComposerContext();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const saveRef = useRef<(() => void) | null>(null);
  const { setIsAutosaving } = useAutosaveContext();

  const save = () => {
    // Export to markdown using our custom PARAGRAPH transformer
    // which handles LineBreakNodes as <br /> and empty paragraphs as <p><br /></p>
    // This is a read-only operation - no editor state modification needed
    editor.read(() => {
      const rawMarkdown = $convertToMarkdownString(PLAYGROUND_TRANSFORMERS);

      // Normalize to ensure proper double newlines between paragraphs
      // (Lexical's export may use single newlines for truly empty paragraphs)
      const markdown = normalizeExportedMarkdown(rawMarkdown);

      onChange(markdown);
      setIsAutosaving(false);
    });
  };

  // Keep saveRef updated with the latest save function
  saveRef.current = save;

  // Set up the triggerAutosave ref so external components can trigger autosave
  useEffect(() => {
    if (triggerAutosaveRef) {
      triggerAutosaveRef.current = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setIsAutosaving(true);
        // Use saveRef.current to always call the latest save function
        timeoutRef.current = setTimeout(() => saveRef.current?.(), throttleMs);
      };
    }
    return () => {
      if (triggerAutosaveRef) {
        triggerAutosaveRef.current = null;
      }
    };
  }, [triggerAutosaveRef, throttleMs, setIsAutosaving]);

  const isClickOutsideEditor = (target: EventTarget | null): boolean => {
    const rootElement = editor.getRootElement();
    if (!rootElement || !target) return false;
    return !rootElement.contains(target as Node);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (isClickOutsideEditor(event.target) && timeoutRef.current) {
      // Click is outside editor and there's a pending autosave
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      // Perform save immediately using the latest save function
      saveRef.current?.();
    }
  };

  useEffect(() => {
    const triggerAutosave = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setIsAutosaving(true);
      // Use saveRef.current to always call the latest save function
      timeoutRef.current = setTimeout(() => saveRef.current?.(), throttleMs);
    };

    // Use Lexical's update listener to detect all content changes
    // This catches typing, formatting (Cmd+B, toolbar clicks), deletions, etc.
    const unregisterUpdateListener = editor.registerUpdateListener(
      ({ dirtyElements, dirtyLeaves, tags }) => {
        // Skip if it's a history merge (undo/redo handled separately)
        // or initial import (page load shouldn't trigger autosave)
        if (
          tags.has("history-merge") ||
          tags.has("initial-import") ||
          tags.has("signature-table-update")
        ) {
          return;
        }

        // Only trigger autosave if there are actual content changes
        // dirtyElements: changed element nodes (paragraphs, headings, etc.)
        // dirtyLeaves: changed leaf nodes (text, formatting changes)
        const hasContentChanges =
          dirtyElements.size > 0 || dirtyLeaves.size > 0;

        if (hasContentChanges) {
          triggerAutosave();
        }
      }
    );

    // Listen for clicks outside the editor
    document.addEventListener("mousedown", handleClickOutside, true);

    return () => {
      unregisterUpdateListener();
      document.removeEventListener("mousedown", handleClickOutside, true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setIsAutosaving(false);
    };
  }, [
    editor,
    throttleMs,
    // Note: onChange and updateType are intentionally excluded from dependencies.
    // onChange is accessed via saveRef.current which always has the latest version.
    // updateType is a ref whose identity never changes.
    // Including them would cause the useEffect to re-run on parent re-renders,
    // canceling pending autosaves.
    setIsAutosaving,
  ]);

  return null;
};

export default AutoSavePlugin;
